import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';

export type PublicationTypeValue = 'application_form' | 'funnel';

export interface PublicationTypeState {
  isFunnel: boolean;
  optimizeContent: boolean;
  generateQuestions: boolean;
  contactEmails: string[];
}

type Translate = (key: string) => string;

// written for the model, not shown in the ui — keep them factual so it can explain the choice
const APPLICATION_FORM_EXPLANATION =
  'A classic online job ad with an application form where candidates upload their CV. ' +
  'Fewer but more complete applications; suits roles where a CV matters.';
const FUNNEL_EXPLANATION =
  'A short, mobile-first, multi-step lead form: candidates answer a few quick questions and need no CV. ' +
  'Lowers the barrier to apply, brings in more but less qualified contacts; suits social media and lead campaigns.';

export const publicationTypeStepDescription =
  'The user picks how candidates apply (publication_type: application form or funnel), whether AI should rewrite the job ad ' +
  '(optimize_content) and generate qualification questions (generate_questions), and who gets notified about new ' +
  'applications (contacts, at least one email required to continue). Change them with booking_publication_type_set. ' +
  'Nothing is saved here: the publication is (re)generated from these settings on the next step.';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Label keys that depend on the selected type — shared by the step template and the on-screen state. */
export function publicationTypeLabelKeys(isFunnel: boolean) {
  return isFunnel
    ? {
        settings: 'order_creation.publication_type.settings',
        settingsHint: 'order_creation.publication_type.settings_hint',
        optimizeTitle: 'order_creation.publication_type.optimize_title',
        optimizeHint: 'order_creation.publication_type.optimize_hint',
      }
    : {
        settings: 'order_creation.publication_type.content_settings',
        settingsHint: 'order_creation.publication_type.content_settings_hint',
        optimizeTitle: 'order_creation.publication_type.optimize_content_title',
        optimizeHint: 'order_creation.publication_type.optimize_content_hint',
      };
}

/** Everything the publication type step shows, labelled the way the user sees it. */
export function buildPublicationTypeOnScreen(state: PublicationTypeState, t: Translate) {
  const { isFunnel } = state;
  const labelKeys = publicationTypeLabelKeys(isFunnel);

  return {
    publication_type: (isFunnel ? 'funnel' : 'application_form') as PublicationTypeValue,
    options: [
      {
        value: 'application_form',
        label: t('order_creation.publication_type.online.title'),
        hint: t('order_creation.publication_type.online.hint'),
        selected: !isFunnel,
        explanation: APPLICATION_FORM_EXPLANATION,
      },
      {
        value: 'funnel',
        label: t('order_creation.publication_type.funnel.title'),
        hint: t('order_creation.publication_type.funnel.hint'),
        selected: isFunnel,
        explanation: FUNNEL_EXPLANATION,
      },
    ],
    settings_heading: t(labelKeys.settings),
    optimize_content: {
      value: state.optimizeContent,
      label: t(labelKeys.optimizeTitle),
      hint: t(labelKeys.optimizeHint),
    },
    generate_questions: {
      value: state.generateQuestions,
      label: t('order_creation.publication_type.questions_title'),
      hint: t('order_creation.publication_type.questions_hint'),
    },
    contacts: {
      emails: [...state.contactEmails],
      required: true,
      label: t('order_creation.publication_type.contact.title'),
      hint: t('order_creation.publication_type.contact.subtitle'),
    },
  };
}

export type PublicationTypeUpdate = Partial<PublicationTypeState>;

/** Validates raw function args into a partial update, or an error the model can act on. */
export function parsePublicationTypeArgs(
  rawArgs: unknown
): { ok: true; update: PublicationTypeUpdate } | { ok: false; error: string } {
  const args = (rawArgs ?? {}) as Record<string, unknown>;
  const update: PublicationTypeUpdate = {};

  if (args.publication_type !== undefined) {
    if (args.publication_type !== 'application_form' && args.publication_type !== 'funnel') {
      return { ok: false, error: 'publication_type must be "application_form" or "funnel".' };
    }
    update.isFunnel = args.publication_type === 'funnel';
  }

  for (const [arg, field] of [
    ['optimize_content', 'optimizeContent'],
    ['generate_questions', 'generateQuestions'],
  ] as const) {
    if (args[arg] === undefined) continue;
    if (typeof args[arg] !== 'boolean') return { ok: false, error: `${arg} must be a boolean.` };
    update[field] = args[arg] as boolean;
  }

  if (args.contact_emails !== undefined) {
    if (!Array.isArray(args.contact_emails)) return { ok: false, error: 'contact_emails must be a list of emails.' };
    const emails = [
      ...new Set(args.contact_emails.map((email) => String(email ?? '').trim()).filter((email) => email.length)),
    ];
    if (!emails.length) return { ok: false, error: 'contact_emails needs at least one email.' };
    const invalid = emails.filter((email) => !EMAIL_PATTERN.test(email));
    if (invalid.length) return { ok: false, error: `Invalid email(s): ${invalid.join(', ')}` };
    update.contactEmails = emails;
  }

  if (!Object.keys(update).length) {
    return {
      ok: false,
      error: 'Pass at least one of publication_type, optimize_content, generate_questions, contact_emails.',
    };
  }

  return { ok: true, update };
}

export interface SetPublicationTypeDeps {
  apply: (update: PublicationTypeUpdate) => void;
  getOnScreen: () => ReturnType<typeof buildPublicationTypeOnScreen>;
}

export function createSetPublicationTypeFunction(deps: SetPublicationTypeDeps): AutopilotFunctionDefinition {
  return {
    name: 'booking_publication_type_set',
    description:
      "Change the wizard's publication type step, same as the user clicking the cards, switches and contacts. " +
      'Only the values you pass change. Nothing is saved yet: the publication is generated from these settings on the next step. ' +
      'Returns the updated on-screen state (also in context.booking_step.on_screen).',
    parameters: {
      type: 'object',
      properties: {
        publication_type: {
          type: 'string',
          enum: ['application_form', 'funnel'],
          description: 'application_form = classic form with CV upload; funnel = short mobile lead form without CV.',
        },
        optimize_content: {
          type: 'boolean',
          description: 'Let AI rewrite and improve the job ad content.',
        },
        generate_questions: {
          type: 'boolean',
          description: 'Let AI generate 3–4 qualification questions from the job ad.',
        },
        contact_emails: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Replaces the full list of emails notified about new applications. At least one; keep the existing ones unless asked to remove them.',
        },
      },
    },
    handler: async (rawArgs) => {
      const parsed = parsePublicationTypeArgs(rawArgs);
      if (!parsed.ok) return { success: false, error: parsed.error };
      deps.apply(parsed.update);
      return { success: true, on_screen: deps.getOnScreen() };
    },
  };
}
